import api from './api';

// Marketplace hardware orders (migration 053): a software provider's purchase
// of boards from a hardware provider. Buyer checkout creates them; the
// seller's Orders surface lists and fulfils them.

export type OrderStatus = 'pending' | 'paid' | 'shipped' | 'completed' | 'cancelled';

export interface HardwareOrder {
  id: number;
  order_number: string;
  manufacturer_id: number;
  company_id: string | number;
  company_name: string;
  status: OrderStatus;
  currency: string;
  subtotal_cents: number;
  platform_fee_cents: number;
  /** 'card' = charged via Stripe; 'terms' = seller-extended net billing. */
  payment_terms?: 'card' | 'terms';
  /** Set when a card charge (checkout or retry) didn't complete; cleared on success. Only meaningful when payment_terms === 'card'. */
  payment_failure_reason?: string | null;
  shipping_details?: Record<string, string> | null;
  billing_details?: Record<string, string> | null;
  shipping_name: string | null;
  shipping_address: string | null;
  carrier: string | null;
  tracking_number: string | null;
  shipped_at: string | null;
  completed_at: string | null;
  notes: string | null;
  created_at: string;
  // list rollups
  items_count?: number;
  units?: number;
  registered_count?: number;
}

export interface RegisteredDevice {
  device_id: string;
  name: string | null;
  status: string;
  deployed: boolean;
}

export interface HardwareOrderItem {
  id: number;
  board_id: number;
  board: string;
  board_display_name: string | null;
  variant_id: number | null;
  variant_name: string | null;
  variant_sku: string | null;
  description: string | null;
  quantity: number;
  unit_price_cents: number;
  monthly_fee_cents: number;
}

export interface HardwareOrderDetail extends HardwareOrder {
  items: HardwareOrderItem[];
  registered_devices: RegisteredDevice[];
  registered_count: number;
}

export async function getManufacturerOrders(
  manufacturerId: number,
  options: { status?: OrderStatus; limit?: number; offset?: number } = {}
): Promise<{ orders: HardwareOrder[]; total: number }> {
  const params = new URLSearchParams();
  if (options.status) params.set('status', options.status);
  if (options.limit) params.set('limit', String(options.limit));
  if (options.offset) params.set('offset', String(options.offset));
  const qs = params.toString();
  const response = await api.get(`/manufacturers/${manufacturerId}/orders${qs ? `?${qs}` : ''}`);
  return response.data;
}

export async function getManufacturerOrder(manufacturerId: number, orderId: number): Promise<HardwareOrderDetail> {
  const response = await api.get(`/manufacturers/${manufacturerId}/orders/${orderId}`);
  return response.data;
}

/**
 * Register the shipped units' device ids — each is assigned to the buying
 * software provider. All-or-nothing; a 400's `details` names problem ids.
 */
export async function registerOrderDevices(
  manufacturerId: number,
  orderId: number,
  deviceIds: string[]
): Promise<{ registered: string[]; registeredCount: number; orderedUnits: number }> {
  const response = await api.post(`/manufacturers/${manufacturerId}/orders/${orderId}/devices`, { deviceIds });
  return response.data;
}

/** Close out a shipped, fully registered order. */
export async function completeManufacturerOrder(manufacturerId: number, orderId: number): Promise<HardwareOrder> {
  const response = await api.post(`/manufacturers/${manufacturerId}/orders/${orderId}/complete`);
  return response.data;
}

/** Terms flow: seller accepts a pending terms order — it becomes fulfillable. */
export async function acceptTermsOrder(manufacturerId: number, orderId: number): Promise<HardwareOrder> {
  const response = await api.post(`/manufacturers/${manufacturerId}/orders/${orderId}/accept-terms`);
  return response.data;
}

/** Terms flow: seller declines a pending terms order → cancelled. */
export async function declineTermsOrder(manufacturerId: number, orderId: number): Promise<HardwareOrder> {
  const response = await api.post(`/manufacturers/${manufacturerId}/orders/${orderId}/decline-terms`);
  return response.data;
}

/** Fulfilment: mark a paid order shipped. */
export async function shipManufacturerOrder(
  manufacturerId: number,
  orderId: number,
  data: { carrier?: string; trackingNumber?: string }
): Promise<HardwareOrder> {
  const response = await api.post(`/manufacturers/${manufacturerId}/orders/${orderId}/ship`, data);
  return response.data;
}
